import Switch from "@components/Switch";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldCheckbox } from "@emerald-react/checkbox";
import { EmeraldInput } from "@emerald-react/input";
import { EmeraldSideSheet } from "@emerald-react/side-sheet";
import useSurveyState from "@hooks/useSurveyState";
import { useUpdateSurveyFlagsMutation } from "@services/survey-core";
import { useGetTourStopsQuery } from "@services/survey-tour-stops";
import { updateCurrentSurvey } from "@survey/surveySlice";
import { BaseSyntheticEvent, useState } from "react";
import TourScheduleDrawer from "./TourScheduleDrawer";
import {
  TOUR_SCHEDULE_FIELD_OPTIONS,
  type TourScheduleFieldKey,
} from "./tourScheduleFieldConfig";

const TourScheduleSection = () => {
  // let currentDate = new Date();
  // let currentYear = currentDate.getFullYear();
  const { survey, dispatch, updateSurvey } = useSurveyState();
  const [updateSurveyFlags] = useUpdateSurveyFlagsMutation();
  const [isTourScheduleOpen, setIsTourScheduleOpen] = useState(false);

  // Prefetch tour stops when survey loads so data is ready when drawer opens
  useGetTourStopsQuery(survey?.id || "", {
    skip: !survey?.id,
  });

  // Track the original value from the backend for comparing on blur
  const [originalTourDetails, setOriginalTourDetails] = useState(
    survey?.tourDetails || "",
  );

  // Use survey value directly for current input
  const tourDetails = survey?.tourDetails || "";
  const fieldVisibility: Record<TourScheduleFieldKey, boolean> = {
    propertyName: survey?.isIncludeTourPropertyName !== false,
    streetAddress: survey?.isIncludeTourStreetAddress !== false,
    addressLine2: survey?.isIncludeTourAddressLine2 !== false,
    city: survey?.isIncludeTourCity !== false,
    state: survey?.isIncludeTourState !== false,
    zipCode: survey?.isIncludeTourZipCode !== false,
  };
  const showDirections = survey?.isIncludeTourArrow !== false;

  const handleClick = () => {
    setIsTourScheduleOpen(true);
  };

  const handleClose = () => {
    setIsTourScheduleOpen(false);
  };

  // Handle input changes for live preview (onChange)
  const handleTourDetailsChange = (e: BaseSyntheticEvent) => {
    const value = e.target.value;

    // Update Redux state immediately for live preview
    dispatch(updateCurrentSurvey({ tourDetails: value }));
  };

  // Handle blur event for API call (onBlur)
  const handleInputBlur = () => {
    // Trim the value when user finishes editing
    const finalTourDetails = (tourDetails || "").trim();

    // Only make API call if the value has actually changed from the original backend value
    if (survey && finalTourDetails !== originalTourDetails) {
      updateSurvey({
        id: survey.id,
        tourDetails: finalTourDetails,
      })
        .unwrap()
        .then(() => {
          // Tour details updated successfully
          // Update the original value after successful API call
          setOriginalTourDetails(finalTourDetails);
        })
        .catch((_error) => {
          console.error("Failed to update tour details");
          // Revert to original value on error
          dispatch(updateCurrentSurvey({ tourDetails: originalTourDetails }));
        });
    } else if (finalTourDetails !== tourDetails) {
      // Even if value hasn't changed, update Redux with trimmed value
      dispatch(updateCurrentSurvey({ tourDetails: finalTourDetails }));
    }
  };

  const handleFieldVisibilityToggle =
    (fieldKey: TourScheduleFieldKey) => (checked: boolean) => {
      if (!survey?.id) {
        return;
      }

      const fieldNameByKey: Record<TourScheduleFieldKey, string> = {
        propertyName: "isIncludeTourPropertyName",
        streetAddress: "isIncludeTourStreetAddress",
        addressLine2: "isIncludeTourAddressLine2",
        city: "isIncludeTourCity",
        state: "isIncludeTourState",
        zipCode: "isIncludeTourZipCode",
      };

      updateSurveyFlags({
        id: survey.id,
        [fieldNameByKey[fieldKey]]: checked,
      });
    };

  const handleShowDirectionsToggle = (checked: boolean) => {
    if (!survey?.id) {
      return;
    }

    updateSurveyFlags({
      id: survey.id,
      isIncludeTourArrow: checked,
    });
  };

  return (
    <section className="flex flex-col gap-y-4 w-full">
      <EmeraldButton
        icon="add"
        size="Small"
        label="Add & Edit Tour Details"
        type="TextButton"
        onClick={handleClick}
      />
      <EmeraldInput
        label="Tour Schedule Header"
        type="text"
        placeholder="Tour Schedule Header"
        className="text-2xl"
        value={tourDetails}
        onChange={handleTourDetailsChange}
        onBlur={handleInputBlur}
        id="SC-SurveySubtitle"
      />
      <div className="rounded-sm border border-divider-1 bg-white p-4">
        <p className="mb-3 text-sm font-medium text-data">Visible Fields</p>
        <div className="grid gap-3 md:grid-cols-2">
          {TOUR_SCHEDULE_FIELD_OPTIONS.map((field) => (
            <div
              key={field.key}
              className="flex items-center justify-between gap-4"
            >
              <span className="text-sm text-data">{field.label}</span>
              <Switch
                checked={fieldVisibility[field.key]}
                size="small"
                onChange={handleFieldVisibilityToggle(field.key)}
              />
            </div>
          ))}
        </div>
        <div className="mt-4 border-t border-divider-1 pt-4">
          <EmeraldCheckbox
            label="Show Directions Arrow"
            checked={showDirections}
            onChange={handleShowDirectionsToggle}
          />
        </div>
      </div>
      <EmeraldSideSheet
        opened={isTourScheduleOpen}
        position={"right"}
        closeOnOutsideClick={false}
        hasCloseButton={true}
        closeOnEscape={false}
        onClose={handleClose}
        className="tour-schedule-drawer"
      >
        <TourScheduleDrawer onClose={handleClose} />
      </EmeraldSideSheet>
    </section>
  );
};

export default TourScheduleSection;
